import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEFAULT_FREE_TIP_IMAGE = 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=600';

const getImgUrl = (url) => {
  if (!url || url === 'None' || url === 'null') return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/uploads/')) return `${BACKEND_URL}${url}`;
  return url;
};

function Dashboard({ onLogout }) {
  const [tips, setTips] = useState([]);
  const [wins, setWins] = useState([]);
  const [news, setNews] = useState([]);
  const [user, setUser] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tips');
  const [sportFilter, setSportFilter] = useState('all');
  const [telegramLink, setTelegramLink] = useState('');
  const [freeTipImage, setFreeTipImage] = useState(DEFAULT_FREE_TIP_IMAGE);
  const [showProfileReminder, setShowProfileReminder] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [expandedTips, setExpandedTips] = useState({});
  const navigate = useNavigate();

  const fetchDashboard = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) { navigate('/'); return; }
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const [userRes, tipsRes, subRes, winsRes, settingsRes, newsRes] = await Promise.all([
        axios.get(`${API}/auth/me`),
        axios.get(`${API}/tips`),
        axios.get(`${API}/subscription/status`),
        axios.get(`${API}/wins`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/news`).catch(() => ({ data: [] })),
      ]);

      setUser(userRes.data);
      setTips(tipsRes.data);
      setSubscriptionStatus(subRes.data);
      setWins(winsRes.data);
      setTelegramLink(settingsRes.data.telegram_link);
      if (settingsRes.data.free_tip_image) setFreeTipImage(settingsRes.data.free_tip_image);
      setNews(newsRes.data);

      if (!userRes.data.first_name || !userRes.data.last_name) {
        setShowProfileReminder(true);
      }

      tipsRes.data.forEach(tip => {
        axios.post(`${API}/tips/${tip.id}/view`).catch(() => {});
      });
      axios.post(`${API}/heartbeat`).catch(() => {});
    } catch (error) {
      if (error.response?.status === 401) { localStorage.removeItem('token'); navigate('/'); }
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);
  useEffect(() => {
    const interval = setInterval(() => axios.post(`${API}/heartbeat`).catch(() => {}), 60000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    if (onLogout) onLogout();
    navigate('/');
  };

  const handleSubscribe = async () => {
    try {
      const res = await axios.post(`${API}/subscription/create-checkout`);
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
      else if (res.data.url) window.location.href = res.data.url;
    } catch (error) { alert(error.response?.data?.detail || 'Unable to start checkout'); }
  };

  const isSubscribed = subscriptionStatus?.status === 'active' || subscriptionStatus?.status === 'cancelled';
  const displayName = user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user?.email?.split('@')[0];

  const filteredTips = sportFilter === 'all' ? tips : tips.filter(t => (t.sport_type || 'soccer') === sportFilter);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e27] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0e27]" data-testid="dashboard-page">
      {/* Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setSelectedImage(null)}>
          <img src={selectedImage} alt="Full" className="max-w-full max-h-[90vh] rounded-xl object-contain" />
        </div>
      )}

      {/* Profile Reminder Modal */}
      {showProfileReminder && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0f1535] rounded-2xl border border-emerald-500/20 p-6 max-w-sm w-full text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <svg className="w-7 h-7 text-emerald-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" /></svg>
            </div>
            <h3 className="text-xl font-extrabold text-white">Complete Your Profile</h3>
            <p className="text-white/40 text-sm mt-2 mb-5">Add your details to get the full experience</p>
            <div className="flex gap-3">
              <button onClick={() => setShowProfileReminder(false)} className="flex-1 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white/60 font-bold text-sm hover:bg-white/10 transition-all">Later</button>
              <button onClick={() => navigate('/profile')} className="flex-1 py-2.5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl font-extrabold text-sm hover:brightness-110 transition-all" data-testid="go-to-profile-button">Complete Now</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#0a0e27]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Logo" className="h-9 w-9 rounded-lg flex-shrink-0" />
            <div>
              <h1 className="text-sm font-extrabold text-white tracking-tight leading-none">Sports Predictions</h1>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-white/40 text-xs">{displayName}</span>
                {isSubscribed && (
                  <span className="bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded text-[9px] font-extrabold border border-emerald-500/30">VIP</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/profile')} className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-all overflow-hidden" data-testid="profile-button">
              {getImgUrl(user?.profile_photo) ? (
                <img
                  src={getImgUrl(user.profile_photo)}
                  alt=""
                  className="w-9 h-9 rounded-lg object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <svg className="w-4 h-4 text-white/40" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" /></svg>
              )}
            </button>
            <button onClick={() => navigate('/profile')} className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-all" data-testid="settings-button" title="Settings">
              <svg className="w-4 h-4 text-white/40" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" /></svg>
            </button>
            <button onClick={handleLogout} className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-red-500/20 transition-all" data-testid="logout-button">
              <svg className="w-4 h-4 text-white/40" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1V4a1 1 0 00-1-1H3zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" /></svg>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-5">
        {/* VIP CTA Banner */}
        {!isSubscribed && (
          <div className="mb-6 relative overflow-hidden rounded-2xl border border-emerald-500/20" data-testid="vip-cta-banner">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 via-transparent to-emerald-500/10"></div>
            <div className="relative p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-1.5 bg-emerald-500/20 text-emerald-400 px-2.5 py-0.5 rounded-full text-[10px] font-extrabold tracking-wider mb-2">
                  <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span> UNLOCK ALL PICKS
                </div>
                <h2 className="text-xl font-extrabold text-white tracking-tight">GO VIP — $10/WEEK</h2>
                <p className="text-white/40 text-xs mt-0.5">NBA + Soccer + MLB predictions. Cancel anytime.</p>
              </div>
              <button onClick={handleSubscribe}
                className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-extrabold rounded-xl hover:brightness-110 transition-all text-sm whitespace-nowrap shadow-lg shadow-emerald-500/20"
                data-testid="subscribe-button">
                SUBSCRIBE NOW
              </button>
            </div>
          </div>
        )}

        {/* Tab Bar */}
        <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
          {[
            { id: 'tips', label: 'Predictions', count: filteredTips.length },
            { id: 'wins', label: 'Our Wins', count: wins.length },
            ...(news.length > 0 ? [{ id: 'news', label: 'News', count: news.length }] : []),
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2.5 rounded-xl text-sm font-extrabold whitespace-nowrap transition-all ${
                activeTab === tab.id ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20' : 'bg-white/5 text-white/50 hover:bg-white/10 border border-white/10'
              }`}
              data-testid={`${tab.id}-tab`}>
              {tab.label} <span className="opacity-60 ml-0.5">({tab.count})</span>
            </button>
          ))}
        </div>

        {/* ====== PREDICTIONS TAB ====== */}
        {activeTab === 'tips' && (
          <>
            <div className="mb-6">
              <h2 className="text-3xl font-extrabold text-white mb-1 tracking-tight">
                {isSubscribed ? 'All Predictions' : "Today's Predictions"}
              </h2>
              <p className="text-white/40 text-sm">Expert picks updated daily</p>
            </div>

            {/* Sport Filter Buttons */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
              {[
                { id: 'all', label: 'All Sports', emoji: '🏆' },
                { id: 'nba', label: 'NBA', emoji: '🏀' },
                { id: 'soccer', label: 'Soccer', emoji: '⚽' },
                { id: 'mlb', label: 'MLB', emoji: '⚾' },
              ].map(sport => (
                <button key={sport.id} onClick={() => setSportFilter(sport.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-extrabold whitespace-nowrap transition-all border ${
                    sportFilter === sport.id
                      ? 'bg-emerald-500 text-white border-emerald-500/50 shadow-lg shadow-emerald-500/20'
                      : 'bg-white/5 text-white/50 border-white/10 hover:bg-white/10'
                  }`}
                  data-testid={`sport-filter-${sport.id}`}>
                  <span className="text-base">{sport.emoji}</span>
                  {sport.label}
                </button>
              ))}
            </div>

            {/* Tip Cards */}
            <div className="space-y-4">
              {filteredTips.length === 0 ? (
                <div className="bg-white/5 rounded-2xl border border-white/10 p-12 text-center">
                  <p className="text-white/40">No predictions for this sport yet</p>
                </div>
              ) : filteredTips.map((tip, index) => {
                const canView = tip.is_free || isSubscribed;
                const tipImage = getImgUrl(tip.image_url) || (tip.is_free ? freeTipImage : null);
                const result = (tip.result || 'pending').toLowerCase();
                const sportLabel = (tip.sport_type || 'soccer').toUpperCase();
                const sportEmojis = { nba: '🏀', mlb: '⚾', soccer: '⚽' };
                const sportEmoji = sportEmojis[tip.sport_type] || '⚽';

                const resultStyles = {
                  win: 'bg-emerald-500/20 text-emerald-400 border-emerald-400/30',
                  loss: 'bg-red-500/20 text-red-400 border-red-400/30',
                };
                const resultLabels = { win: '✅ WIN', loss: '❌ LOSS' };
                const resultStyle = resultStyles[result] || 'bg-white/5 text-white/40 border-white/10';
                const resultLabel = resultLabels[result] || '⏳ PENDING';

                return (
                  <div
                    key={tip.id}
                    className={`bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden transition-all duration-300 hover:bg-white/8 relative border ${
                      !tip.is_free ? 'border-amber-500/15 hover:border-amber-500/30' : 'border-white/10 hover:border-white/20'
                    }`}
                    style={{ animationDelay: `${index * 0.08}s`, animation: 'fadeSlideUp 0.5s ease forwards', opacity: 0 }}
                    data-testid={`tip-card-${tip.id}`}
                  >
                    {/* Top accent line */}
                    <div className={`h-0.5 ${tip.is_free ? 'bg-gradient-to-r from-emerald-400 to-cyan-400' : 'bg-gradient-to-r from-amber-400 to-orange-500'}`}></div>

                    {/* Header with badges */}
                    <div className="flex items-center justify-between px-4 pt-4 pb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-extrabold ${
                          tip.is_free ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                        }`}>
                          {tip.is_free ? 'FREE' : 'VIP'}
                        </span>
                        <span className="bg-white/10 text-white/70 px-2.5 py-0.5 text-[10px] font-extrabold rounded-full border border-white/10">
                          {sportEmoji} {sportLabel}
                        </span>
                        <span className={`px-2.5 py-0.5 text-[10px] font-extrabold rounded-full border ${resultStyle}`}>
                          {resultLabel}
                        </span>
                      </div>
                    </div>

                    {canView ? (
                      <>
                        <div className="px-4 pb-3">
                          <h3 className="text-base font-extrabold text-white tracking-tight">{tip.title}</h3>
                          {tip.description && (
                            <div className="mt-2">
                              <p className={`text-white/50 text-sm leading-relaxed whitespace-pre-line ${!expandedTips[tip.id] ? 'line-clamp-3' : ''}`}>
                                {tip.description}
                              </p>
                              {tip.description.split('\n').length > 3 || tip.description.length > 150 ? (
                                <button
                                  onClick={() => setExpandedTips(prev => ({ ...prev, [tip.id]: !prev[tip.id] }))}
                                  className="text-emerald-400 hover:text-emerald-300 text-xs font-bold mt-1.5 transition-colors"
                                  data-testid={`tip-see-more-${tip.id}`}
                                >
                                  {expandedTips[tip.id] ? 'See Less' : 'See More'}
                                </button>
                              ) : null}
                            </div>
                          )}
                        </div>
                        {tipImage && (
                          <div className="px-4 pb-3">
                            <img src={tipImage} alt={tip.title} className="w-full rounded-xl object-cover max-h-64 cursor-pointer hover:opacity-90 transition-opacity" onClick={() => setSelectedImage(tipImage)} />
                          </div>
                        )}
                        {(tip.parlay_link || tip.link_url) && (
                          <div className="px-4 pb-4 flex gap-2 flex-wrap">
                            {tip.parlay_link && (
                              <a href={tip.parlay_link} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-lg text-xs font-bold hover:bg-emerald-500/20 transition-all">
                                🎯 Parlay Link
                              </a>
                            )}
                            {tip.link_url && (
                              <a href={tip.link_url} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 px-4 py-2 bg-white/5 text-white/60 border border-white/10 rounded-lg text-xs font-bold hover:text-white transition-all">
                                🔗 View Link
                              </a>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="px-4 pb-4">
                        <div className="bg-gradient-to-br from-white/5 to-white/[0.02] rounded-xl border border-white/10 p-6 text-center">
                          <svg className="w-8 h-8 text-amber-400/50 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
                          <p className="text-white/40 text-sm font-bold mb-3">VIP Content — Unlock All Picks</p>
                          <button onClick={handleSubscribe}
                            className="px-5 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg text-xs font-extrabold hover:brightness-110 transition-all"
                            data-testid="tip-subscribe-button">
                            SUBSCRIBE — $10/week
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ====== WINS TAB ====== */}
        {activeTab === 'wins' && (
          <>
            <div className="mb-6">
              <h2 className="text-3xl font-extrabold text-white tracking-tight">Our Track Record</h2>
              <p className="text-white/40 text-sm mt-1">Verified wins from our predictions</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {wins.length === 0 ? (
                <div className="col-span-full bg-white/5 rounded-2xl border border-white/10 p-12 text-center">
                  <p className="text-white/40">Wins coming soon</p>
                </div>
              ) : wins.map((win, i) => (
                <div key={win.id}
                  className="bg-white/5 rounded-2xl border border-emerald-500/10 overflow-hidden hover:border-emerald-500/25 transition-all"
                  style={{ animationDelay: `${i * 0.06}s`, animation: 'fadeSlideUp 0.5s ease forwards', opacity: 0 }}
                  data-testid={`win-card-${win.id}`}>
                  {win.image_url && (
                    <img src={getImgUrl(win.image_url)} alt={win.match_name} className="w-full h-44 object-cover cursor-pointer hover:opacity-90 transition-opacity" onClick={() => setSelectedImage(getImgUrl(win.image_url))} />
                  )}
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-emerald-400 font-extrabold text-lg">{win.amount_won}</span>
                      <span className="text-white/30 text-xs">{win.date}</span>
                    </div>
                    <h3 className="text-white font-bold text-sm">{win.match_name}</h3>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ====== NEWS TAB ====== */}
        {activeTab === 'news' && (
          <>
            <div className="mb-6">
              <h2 className="text-3xl font-extrabold text-white tracking-tight">News & Content</h2>
              <p className="text-white/40 text-sm mt-1">Latest articles and resources for members</p>
            </div>
            <div className="space-y-3">
              {news.map((item, i) => (
                <a key={item.id} href={item.url} target="_blank" rel="noopener noreferrer"
                  className="block bg-white/5 rounded-2xl border border-white/10 hover:border-emerald-500/20 p-5 transition-all"
                  style={{ animationDelay: `${i * 0.06}s`, animation: 'fadeSlideUp 0.5s ease forwards', opacity: 0 }}
                  data-testid={`news-item-${item.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {item.is_vip_only && <span className="bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded text-[10px] font-extrabold border border-amber-500/20">VIP</span>}
                        <h3 className="text-white font-bold text-sm truncate">{item.title}</h3>
                      </div>
                      {item.description && <p className="text-white/40 text-xs truncate">{item.description}</p>}
                    </div>
                    <svg className="w-4 h-4 text-white/30 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </a>
              ))}
            </div>
          </>
        )}

        {/* Bottom Links */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          {telegramLink && (
            <a href={telegramLink} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#0088cc]/15 text-[#29B6F6] border border-[#0088cc]/20 rounded-xl text-xs font-bold hover:bg-[#0088cc]/25 transition-all" data-testid="telegram-link">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"/></svg>
              Join Telegram
            </a>
          )}
          <Link to="/terms" className="inline-flex items-center gap-2 px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-xs text-white/40 font-bold hover:text-white/60 transition-all">Terms & Privacy</Link>
          <a href="mailto:support@sportspredictionstips.com" className="inline-flex items-center gap-2 px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-xs text-white/40 font-bold hover:text-white/60 transition-all" data-testid="support-link">Support</a>
        </div>

        <div className="text-center mt-6 pb-8 text-white/20 text-xs">Sports Predictions & Tips</div>
      </div>
    </div>
  );
}

export default Dashboard;
